import {
  VideoClient,
  VideoClientConfig,
  VideoClientEvent,
} from '../types/video-provider'

import ZoomVideo from '@zoom/videosdk'
import { enumerateMediaDevices } from '../utils/mediaDeviceDiagnostics'

// Extend Window interface to include ZoomVideo SDK global
declare global {
  interface Window {
    /** Zoom Video SDK UMD global (set when loaded via script tag) */
    WebVideoSDK?: { default?: any; createClient?: Function } | any
    /** Fallback Zoom global */
    ZoomVideo?: any
    /** js_media constructor on window; static `.util` is wired after patch load */
    JsMediaSDK_Instance?: (new (...args: unknown[]) => unknown) & { util?: Record<string, unknown> }
    __vcCallAudioContext?: AudioContext
    webkitAudioContext?: typeof AudioContext
  }
}

/**
 * Module-level Zoom SDK singleton.
 *
 * The Zoom Video SDK must only be initialized once per browser page — calling
 * createClient() / init() a second time causes "PubSub already loaded" warnings
 * and error 5012 ("duplicated operation") when join() is called while already
 * in a session. Multiple ZoomClient instances (e.g. RoomListView → ActiveCallView
 * navigation) all share the same underlying SDK handle via this singleton.
 */
let _zoomSdkClient: any = null
let _zoomSdkInitialized = false
let _zoomSessionActive = false
let _currentSessionId: string | null = null

/**
 * Get singleton ZoomClient instance
 * Pattern from: https://github.com/zoom/videosdk-web-sample
 * Ensures only one ZoomClient instance per application
 */
let _zoomClientInstance: ZoomClient | null = null

export function getZoomClientInstance(): ZoomClient {
  if (!_zoomClientInstance) {
    _zoomClientInstance = new ZoomClient()
  }
  return _zoomClientInstance
}

/**
 * Zoom Video SDK Client (Frontend)
 * 
 * Frontend implementation for Zoom Video SDK integration.
 * Handles joining sessions, media management, and WebRTC connections.
 * 
 * This runs in the browser and uses the Zoom Video SDK for Web.
 */
export class ZoomClient implements VideoClient {
  readonly name = 'zoom'
  private initialized = false
  private zoomClient: any = null
  private recordingClient: any = null // Zoom RecordingClient for cloud recording
  private liveTranscriptionClient: any = null // Zoom LiveTranscriptionClient for closed-captioning
  private eventHandlers: Map<VideoClientEvent, Set<Function>> = new Map()
  private remoteStreams: Map<string, MediaStream> = new Map()
  private videoPlayerRefs: Map<number, any> = new Map() // Track attached video elements
  private boundHandlers: Map<string, Function> = new Map() // Track bound event handlers for cleanup
  private runtimeErrorHandler: ((event: ErrorEvent) => void) | null = null
  private runtimeRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null
  /** Single-flight: start computer audio before any attachVideo / getMediaStream video path (avoids 6011 races). */
  private _computerAudioEnsurePromise: Promise<void> | null = null
  /** Single-flight: prevent concurrent startVideo calls for local camera (avoids 6105 "Camera is starting"). */
  private _localVideoStartPromise: Promise<void> | null = null
  /**
   * Incremented on leaveSession so in-flight startAudio/getMediaStream loops exit immediately.
   * Prevents stale retries (IMPROPER_MEETING_STATE / 5002) and attachVideo after dispose.
   */
  private _mediaAbortGeneration = 0
  /**
   * Resolved when joinSession() finishes its post-join delay and emits `connected`.
   * peer-video-state-change can fire during that window; attachVideo must await this
   * or getMediaStream/startAudio run before the SDK media bridge is ready (util/6011/timeouts).
   */
  private _joinSessionReadyPromise: Promise<void> = Promise.resolve()
  private _resolveJoinSessionReady: (() => void) | null = null

  /** Transient Zoom join failures (e.g. LSDK JSONP net::ERR_SOCKET_NOT_CONNECTED) often recover on retry. */
  private static isRetryableZoomJoinError(error: unknown): boolean {
    const e = error as { errorCode?: number; type?: string; reason?: string; message?: string }
    if (e?.errorCode === 5012) return false
    const reason = String(e?.reason ?? e?.message ?? '').toLowerCase()
    const type = String(e?.type ?? '')
    if (type === 'JOIN_MEETING_FAILED' && reason.includes('connection')) {
      return true
    }
    return false
  }

  async initialize(_config: VideoClientConfig): Promise<void> {
    try {
      // === DIAGNOSTIC: Check browser capabilities ===
      console.log('=== ZOOM SDK INITIALIZATION DIAGNOSTICS ===')
      console.log('Browser:', navigator.userAgent)
      console.log('crossOriginIsolated:', typeof crossOriginIsolated !== 'undefined' ? crossOriginIsolated : 'undefined')
      console.log('SharedArrayBuffer:', typeof SharedArrayBuffer)
      console.log('getUserMedia:', typeof navigator?.mediaDevices?.getUserMedia)
      console.log('RTCPeerConnection:', typeof RTCPeerConnection)
      console.log('WebAssembly:', typeof WebAssembly)
      console.log('Atomics:', typeof Atomics)

      // Check permissions
      try {
        const camPerm = await navigator.permissions.query({ name: 'camera' as PermissionName })
        const micPerm = await navigator.permissions.query({ name: 'microphone' as PermissionName })
        console.log('Camera permission:', camPerm.state)
        console.log('Microphone permission:', micPerm.state)
      } catch (permErr) {
        console.warn('Could not check permissions:', permErr)
      }
      
      // === ENUMERATE MEDIA DEVICES ===
      console.log('[ZoomClient] Enumerating available media devices...')
      const devices = await enumerateMediaDevices()
      console.log('[ZoomClient] AVAILABLE MEDIA DEVICES:', {
        cameras: devices.cameras.length,
        microphones: devices.microphones.length,
        speakers: devices.speakers.length,
      })
      console.log('[ZoomClient] Cameras:', devices.cameras.map((c, idx) => ({
        index: idx + 1,
        label: c.label,
        deviceId: c.deviceId.substring(0, 20) + '...',
      })))
      console.log('[ZoomClient] Microphones:', devices.microphones.map((m, idx) => ({
        index: idx + 1,
        label: m.label,
        deviceId: m.deviceId.substring(0, 20) + '...',
      })))
      
      console.log('=== END DIAGNOSTICS ===')
      
      // DON'T init the SDK here - wait until join()
      // The official Zoom sample calls init() immediately before join()
      if (!_zoomSdkClient) {
        console.log('[ZoomClient] Creating Zoom SDK client...')
        _zoomSdkClient = ZoomVideo.createClient()
        console.log('[ZoomClient] Zoom SDK client created')
      }

      this.zoomClient = _zoomSdkClient
      // DISABLED: Let Zoom SDK errors surface for debugging
      // this.installRuntimeErrorGuards()

      // Initialize recording client
      this.recordingClient = this.zoomClient.getRecordingClient()

      // Initialize live transcription client (optional - may not be available in all SDK versions)
      try {
        if (typeof this.zoomClient.getLiveTranscriptionClient === 'function') {
          this.liveTranscriptionClient = this.zoomClient.getLiveTranscriptionClient()
          console.log('[ZoomClient] Live transcription client initialized')
        } else {
          console.warn('[ZoomClient] Live transcription client not available in this SDK version')
          this.liveTranscriptionClient = null
        }
      } catch (err) {
        console.warn('[ZoomClient] Failed to initialize live transcription client:', err)
        this.liveTranscriptionClient = null
      }

      // Re-initialize listeners safely for singleton reuse across view mounts.
      this.removeEventListeners()
      this.setupEventListeners()

      // Set initialized to true ONLY after successful init
      this.initialized = true

      console.log('[ZoomClient] Initialized with Zoom Video SDK')
    } catch (error) {
      console.error('[ZoomClient] Failed to initialize Zoom SDK:', error)
      console.warn('[ZoomClient] Install @zoom/videosdk package if using VIDEO_PROVIDER=zoom')
      console.warn('[ZoomClient] Run: npm install @zoom/videosdk --workspace=@virtual-care/shared')
      this.zoomClient = null
      this.initialized = false
      throw error
    }
  }

  /**
   * Join a Zoom session with JWT token.
   * Idempotent — if already in the session (errorCode 5012), emits 'connected'
   * and returns without error. This handles the nurse navigating from
   * RoomListView (where join happens) to ActiveCallView (which calls startCall
   * again after patient accepts).
   */
  async joinSession(sessionId: string, credentials: any): Promise<void> {
    if (!this.initialized) {
      throw new Error('ZoomClient not initialized')
    }

    if (!this.zoomClient) {
      console.warn('[ZoomClient] Zoom SDK not available, cannot join session')
      this.emitEvent('error', new Error('Zoom SDK not available'))
      return
    }

    // CRITICAL: Init the SDK right before FIRST join only
    // The official sample ensures init() is called ONLY ONCE per client
    // Calling init() multiple times breaks the SDK's internal state
    if (!_zoomSdkInitialized) {
      console.log('[ZoomClient] Initializing Zoom SDK before join...')
      const dependentAssets = `${window.location.origin}/lib`
      const initOptions = {
        webEndpoint: 'zoom.us',
        enforceMultipleVideos: true,
        stayAwake: true,
        patchJsMedia: true,
        leaveOnPageUnload: false
      }
      console.log('[ZoomClient] Init options:', { dependentAssets, ...initOptions })
      await this.zoomClient.init('en-US', dependentAssets, initOptions)
      _zoomSdkInitialized = true
      console.log('[ZoomClient] Zoom SDK initialized successfully')
    } else {
      console.log('[ZoomClient] SDK already initialized, skipping init()')
    }

    // Check if already in the SAME session
    if (_zoomSessionActive && _currentSessionId === sessionId) {
      console.log(`[ZoomClient] Already in session ${sessionId}, emitting connected (skipping duplicate join)`)
      this.emitEvent('connected')
      return
    }

    // If in a DIFFERENT session, error (shouldn't happen)
    if (_zoomSessionActive && _currentSessionId !== sessionId) {
      throw new Error(`Already in a different session: ${_currentSessionId}. Leave before joining ${sessionId}`)
    }

    this._joinSessionReadyPromise = new Promise<void>((resolve) => {
      this._resolveJoinSessionReady = resolve
    })

    try {
      const token =
        credentials?.token != null && String(credentials.token).trim() !== ''
          ? String(credentials.token).trim()
          : ''
      const userNameRaw =
        credentials?.userName != null && String(credentials.userName).trim() !== ''
          ? String(credentials.userName).trim()
          : ''
      const userName = userNameRaw.length > 0 ? userNameRaw : 'User'
      const { password } = credentials

      if (!token) {
        const err = new Error(
          'Zoom join requires a non-empty token (session topic, token, and userName).'
        )
        this.emitEvent('error', err)
        throw err
      }

      const maxJoinAttempts = 3
      for (let attempt = 1; attempt <= maxJoinAttempts; attempt++) {
        try {
          await this.zoomClient.join(sessionId, token, userName, password)
          break
        } catch (joinErr: any) {
          if (joinErr?.errorCode === 5012) {
            console.log(`[ZoomClient] Already in session ${sessionId} (errorCode 5012), treating as connected`)
            _zoomSessionActive = true
            _currentSessionId = sessionId
            this.emitEvent('connected')
            return
          }
          const canRetry =
            attempt < maxJoinAttempts && ZoomClient.isRetryableZoomJoinError(joinErr)
          if (canRetry) {
            const delayMs = 1000 * attempt
            console.warn(
              `[ZoomClient] join attempt ${attempt}/${maxJoinAttempts} failed (${String(joinErr?.reason ?? joinErr?.type ?? 'unknown')}), retrying in ${delayMs}ms`
            )
            await new Promise<void>(resolve => setTimeout(resolve, delayMs))
            continue
          }
          throw joinErr
        }
      }

      _zoomSessionActive = true
      _currentSessionId = sessionId

      // LSDK + join handshake: give media bridge time before startAudio/startVideo.
      await new Promise<void>(resolve => setTimeout(resolve, 1200))

      console.log(`[ZoomClient] Joined session: ${sessionId}`)
      this.emitEvent('connected')

      // Scan for participants who were already sending video before we joined.
      // peer-video-state-change only fires for state changes while in session,
      // so we need to check the initial state manually.
      this.emitExistingVideoStreams()
    } catch (error: any) {
      // errorCode 5012 = "duplicated operation" — already in the session (outer catch if join loop missed it)
      if (error?.errorCode === 5012) {
        console.log(`[ZoomClient] Already in session ${sessionId} (errorCode 5012), treating as connected`)
        _zoomSessionActive = true
        _currentSessionId = sessionId
        this.emitEvent('connected')
        return
      }
      console.error('[ZoomClient] Failed to join session:', error)
      this.emitEvent('error', error)
      throw error
    } finally {
      this._resolveJoinSessionReady?.()
      this._resolveJoinSessionReady = null
    }
  }

  /**
   * Leave the current Zoom session
   */
  async leaveSession(): Promise<void> {
    // Invalidate any in-flight ensureComputerAudioStarted / startAudioWithRetry / getZoomMediaStreamWithRetry
    this._mediaAbortGeneration++
    this._computerAudioEnsurePromise = null
    this._localVideoStartPromise = null

    if (!this.initialized || !this.zoomClient) {
      _zoomSessionActive = false
      _currentSessionId = null
      return
    }

    try {
      await this.zoomClient.leave()
      _zoomSessionActive = false
      _currentSessionId = null
      this.remoteStreams.clear()
      this._resolveJoinSessionReady?.()
      this._resolveJoinSessionReady = null
      this._joinSessionReadyPromise = Promise.resolve()

      console.log('[ZoomClient] Left session')
      this.emitEvent('disconnected')
    } catch (error) {
      console.error('[ZoomClient] Failed to leave session:', error)
      _zoomSessionActive = false
      this._resolveJoinSessionReady?.()
      this._resolveJoinSessionReady = null
      this._joinSessionReadyPromise = Promise.resolve()
      this.emitEvent('error', error)
    }
  }

  /**
   * Get local media stream (camera + microphone)
   * 
   * Note: Zoom SDK doesn't provide standard MediaStream objects.
   * Instead, use renderVideo() with a video element to display video.
   */
  async getLocalMedia(_constraints?: MediaStreamConstraints): Promise<MediaStream> {
    if (!this.initialized || !this.zoomClient) {
      throw new Error('ZoomClient not initialized or Zoom SDK not available')
    }

    try {
      // Let Zoom SDK own device acquisition. Pre-opening and closing gUM can
      // race with SDK internals in some Chromium paths.
      console.log('[ZoomClient] Preparing Zoom media stream (computer audio first)...')

      // Try to start audio with timeout, but don't block if it fails due to autoplay restrictions
      try {
        // Add 5-second timeout for audio setup on page refresh scenarios
        await Promise.race([
          this.ensureComputerAudioStarted(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Audio setup timeout - proceeding without audio')), 5000)
          )
        ])
      } catch (audioError) {
        console.warn('[ZoomClient] Audio setup failed or timed out during getLocalMedia (likely autoplay restriction), audio will start on first user interaction:', audioError)
        // Continue - audio can be started later when user interacts
      }

      const zoomStream = Object.assign(new MediaStream(), {
        __isZoomStream: true,
        __zoomClient: this,
      }) as MediaStream

      console.log('[ZoomClient] Local media initialized')
      
      // === GET ACTIVE DEVICE DIAGNOSTICS ===
      // Wait a bit for Zoom SDK to fully initialize media
      setTimeout(async () => {
        try {
          const mediaStream = this.zoomClient?.getMediaStream()
          if (mediaStream) {
            // Try to get active camera info from Zoom SDK
            const cameraList = mediaStream.getCameraList()
            const activeCamera = mediaStream.getActiveCamera()
            
            console.log('[ZoomClient] ACTIVE CAMERA DETECTED:', {
              activeCamera: activeCamera || 'Not started yet',
              availableCameras: cameraList?.length || 0,
              cameraList: cameraList?.map((c: any) => ({
                deviceId: c.deviceId,
                label: c.label,
              })) || [],
            })
            
            // Get microphone info
            const micList = mediaStream.getMicList()
            const activeMic = mediaStream.getActiveMicrophone()
            
            console.log('[ZoomClient] ACTIVE MICROPHONE DETECTED:', {
              activeMicrophone: activeMic || 'Not started yet',
              availableMicrophones: micList?.length || 0,
              microphoneList: micList?.map((m: any) => ({
                deviceId: m.deviceId,
                label: m.label,
              })) || [],
            })
          }
        } catch (err) {
          console.warn('[ZoomClient] Could not get active device info:', err)
        }
      }, 1000)
      
      return zoomStream
    } catch (error) {
      console.error('[ZoomClient] Failed to get local media:', error)
      throw error
    }
  }

  /**
   * Attach video for a participant (or local self-view) to a container element.
   *
   * Zoom SDK v2.x Chromium path (no SharedArrayBuffer):
   * - renderVideo() is deprecated and throws error 6112 on Chromium
   * - attachVideo(userId, quality) creates and returns a managed <video> element
   * - For local self-view: startVideo() first, then attachVideo(myUserId, quality)
   *
   * @param container  The <div> element to append the Zoom-managed <video> into
   * @param userId     The Zoom participant userId. Pass undefined for local self-view.
   * @param options    muted / mirrored flags applied to the returned video element
   */
  async attachVideo(
    container: HTMLElement,
    userId: number | undefined,
    options: { muted?: boolean; mirrored?: boolean } = {}
  ): Promise<void> {
    console.log(`[ZoomClient] attachVideo called: userId=${userId}, container=${!!container}, client=${!!this.zoomClient}`)
    
    if (!this.zoomClient) {
      console.warn('[ZoomClient] Cannot attach video: client not initialized')
      return
    }

    if (!_zoomSessionActive) {
      console.warn('[ZoomClient] attachVideo: no active session')
      return
    }

    const mediaGen = this._mediaAbortGeneration

    await this._joinSessionReadyPromise
    if (
      this._mediaAbortGeneration !== mediaGen ||
      !this.zoomClient ||
      !_zoomSessionActive
    ) {
      console.warn('[ZoomClient] attachVideo: session ended during join stabilization')
      return
    }

    try {
      // Try to start audio with a timeout, but don't block video attachment if it fails
      try {
        // Add 5-second timeout for audio setup on page refresh scenarios
        await Promise.race([
          this.ensureComputerAudioStarted(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Audio setup timeout - proceeding without audio')), 5000)
          )
        ])
      } catch (audioError) {
        console.warn('[ZoomClient] Audio setup failed or timed out (likely autoplay restriction), proceeding with video:', audioError)
        // Continue with video attachment even if audio fails
      }

      if (
        this._mediaAbortGeneration !== mediaGen ||
        !this.zoomClient ||
        !_zoomSessionActive ||
        !this.initialized
      ) {
        console.warn('[ZoomClient] attachVideo: cancelled after audio setup (session ended or disposed)')
        return
      }

      const mediaStream = await this.getZoomMediaStreamWithRetry(12, mediaGen)

      // Clear any previous video element in the container
      container.innerHTML = ''

      // Zoom SDK web component requires VIDEO-PLAYER-CONTAINER ancestor.
      const hostContainer = document.createElement('video-player-container') as HTMLElement
      hostContainer.style.width = '100%'
      hostContainer.style.height = '100%'
      container.appendChild(hostContainer)

      if (userId === undefined) {
        console.log('[ZoomClient] attachVideo: local self-view path')
        // Local self-view: get current user's Zoom-assigned participant ID.
        // Wait for it to become available (may not be set immediately after join).
        let currentUserId: number | undefined
        let attempts = 0
        const maxAttempts = 20 // 2 seconds max

        while (!currentUserId && attempts < maxAttempts) {
          if (!this.zoomClient) {
            console.warn('[ZoomClient] Session closed while waiting for user ID')
            return
          }
          currentUserId = this.zoomClient.getCurrentUserInfo()?.userId
          console.log(`[ZoomClient] Attempt ${attempts + 1}: currentUserId=${currentUserId}`)
          if (!currentUserId) {
            await new Promise(resolve => setTimeout(resolve, 100))
            attempts++
          }
        }

        if (!currentUserId) {
          console.warn('[ZoomClient] Cannot attach local self-view: current user ID not available after waiting')
          return
        }

        console.log(`[ZoomClient] Got current user ID: ${currentUserId}`)

        // Verify client is still alive before proceeding
        if (!this.zoomClient) {
          console.warn('[ZoomClient] Session closed before attaching local video')
          return
        }

        // Official flow: startVideo() then attachVideo(selfUserId) inside video-player-container.
        // The videoElement shortcut path timed out when internal capability/async state was not ready.
        console.log('[ZoomClient] Starting local video (startVideo + attachVideo)...')

        try {
          // Single-flight guard: if video is already starting, wait for it instead of starting again
          if (this._localVideoStartPromise) {
            console.log('[ZoomClient] Local video already starting, waiting for it to complete...')
            await this._localVideoStartPromise
            console.log('[ZoomClient] Local video start completed, proceeding with attach')
          } else {
            // Start video with single-flight promise
            this._localVideoStartPromise = (async () => {
              try {
                await Promise.race([
                  mediaStream.startVideo(),
                  new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error('startVideo() timed out after 45s')), 45000)
                  ),
                ])
                console.log('[ZoomClient] Local video started successfully')
                
                // === COMPREHENSIVE CAMERA DIAGNOSTICS ===
                try {
                  const activeCamera = mediaStream.getActiveCamera()
                  const activeSpeaker = mediaStream.getActiveSpeaker()
                  const activeMic = mediaStream.getActiveMicrophone()
                  
                  // Get all available devices
                  const cameraList = mediaStream.getCameraList()
                  const micList = mediaStream.getMicList()
                  const speakerList = mediaStream.getSpeakerList()
                  
                  console.log('[ZoomClient] === ACTIVE CAMERA DIAGNOSTICS ===')
                  console.log('[ZoomClient] Active Camera:', {
                    deviceId: activeCamera,
                    details: cameraList?.find((c: any) => c.deviceId === activeCamera),
                  })
                  console.log('[ZoomClient] All Available Cameras:', cameraList?.map((c: any, idx: number) => ({
                    index: idx + 1,
                    deviceId: c.deviceId,
                    label: c.label,
                    isActive: c.deviceId === activeCamera,
                  })))
                  
                  console.log('[ZoomClient] Active Microphone:', {
                    deviceId: activeMic,
                    details: micList?.find((m: any) => m.deviceId === activeMic),
                  })
                  console.log('[ZoomClient] All Available Microphones:', micList?.map((m: any, idx: number) => ({
                    index: idx + 1,
                    deviceId: m.deviceId,
                    label: m.label,
                    isActive: m.deviceId === activeMic,
                  })))
                  
                  console.log('[ZoomClient] Active Speaker:', {
                    deviceId: activeSpeaker,
                    details: speakerList?.find((s: any) => s.deviceId === activeSpeaker),
                  })
                  
                  // Emit event with diagnostics for backend logging
                  const diagnosticsPayload = {
                    timestamp: new Date().toISOString(),
                    activeCamera: {
                      deviceId: activeCamera,
                      label: cameraList?.find((c: any) => c.deviceId === activeCamera)?.label || 'Unknown',
                    },
                    activeMicrophone: {
                      deviceId: activeMic,
                      label: micList?.find((m: any) => m.deviceId === activeMic)?.label || 'Unknown',
                    },
                    availableDevices: {
                      cameras: cameraList?.map((c: any) => ({ deviceId: c.deviceId, label: c.label })) || [],
                      microphones: micList?.map((m: any) => ({ deviceId: m.deviceId, label: m.label })) || [],
                      speakers: speakerList?.map((s: any) => ({ deviceId: s.deviceId, label: s.label })) || [],
                    },
                  }
                  
                  console.log('[ZoomClient] EMITTING media_diagnostics event to listeners')
                  this.emitEvent('media_diagnostics', diagnosticsPayload)
                  console.log('[ZoomClient] media_diagnostics event emitted')
                  
                  console.log('[ZoomClient] =====================================')
                } catch (diagErr) {
                  console.warn('[ZoomClient] Could not get camera diagnostics:', diagErr)
                }
              } finally {
                this._localVideoStartPromise = null
              }
            })()
            await this._localVideoStartPromise
          }

          const videoEl = await mediaStream.attachVideo(currentUserId, 3)

          if (options.muted && 'muted' in videoEl) (videoEl as HTMLVideoElement).muted = true
          if (options.mirrored) (videoEl as HTMLElement).style.transform = 'scaleX(-1)'
          videoEl.style.width = '100%'
          videoEl.style.height = '100%'
          videoEl.style.objectFit = 'cover'

          hostContainer.appendChild(videoEl)
          this.videoPlayerRefs.set(currentUserId, videoEl)

          if (typeof (videoEl as HTMLVideoElement).play === 'function') {
            ;(videoEl as HTMLVideoElement).play().catch(e => console.warn('[ZoomClient] Local video play() blocked:', e))
          }
          console.log(`[ZoomClient] Local self-view attached (userId: ${currentUserId}, tag: ${videoEl.tagName})`)
          return
        } catch (startErr: any) {
          const msg = String(startErr?.message ?? startErr)
          const code = Number(startErr?.errorCode ?? -1)
          console.warn('[ZoomClient] Local startVideo/attachVideo failed:', msg, {
            errorCode: code,
            reason: startErr?.reason,
          })
          console.warn('[ZoomClient] Call will proceed without local camera preview.')
          return
        }
      }

      // Remote video: use attachVideo(userId, quality)
      const targetUserId = userId
      console.log(`[ZoomClient] Attaching remote video for userId ${targetUserId}`)

      // Reset host container for remote render.
      container.innerHTML = ''
      const remoteHost = document.createElement('video-player-container') as HTMLElement
      remoteHost.style.width = '100%'
      remoteHost.style.height = '100%'
      container.appendChild(remoteHost)

      // When crossOriginIsolated=true the SDK uses its WASM renderer and returns a
      // <canvas> element. Without it, it returns a <video> element. Both are valid;
      // only <video> has a play() method, so we guard accordingly.
      const videoEl = await mediaStream.attachVideo(targetUserId, 3 /* VideoQuality.Video_360P */)

      if (options.muted && 'muted' in videoEl) (videoEl as HTMLVideoElement).muted = true
      if (options.mirrored) (videoEl as HTMLElement).style.transform = 'scaleX(-1)'
      videoEl.style.width = '100%'
      videoEl.style.height = '100%'
      videoEl.style.objectFit = 'cover'

      remoteHost.appendChild(videoEl)

      // Store reference for cleanup
      this.videoPlayerRefs.set(targetUserId, videoEl)

      // Only call play() on actual <video> elements — <canvas> (WASM path) has no play()
      if (typeof (videoEl as HTMLVideoElement).play === 'function') {
        ;(videoEl as HTMLVideoElement).play().catch(e => console.warn('[ZoomClient] Remote video play() blocked:', e))
      }
      console.log(`[ZoomClient] Video attached for userId ${targetUserId} (tag: ${videoEl.tagName})`)
    } catch (error) {
      if (error instanceof Error && error.message === ZoomClient.ZOOM_MEDIA_ABORTED) {
        console.warn('[ZoomClient] attachVideo: aborted (session ended)')
        return
      }
      console.error('[ZoomClient] Failed to attach video:', error)
      throw error
    }
  }

  /**
   * Detach video element for a specific user
   * MANDATORY: Prevents memory leaks and SDK state corruption
   * Pattern from: https://github.com/zoom/videosdk-web-sample/blob/main/src/feature/video/video-attach.tsx#L113-L116
   */
  async detachVideo(userId: number): Promise<void> {
    const element = this.videoPlayerRefs.get(userId)
    if (!element) {
      console.log(`[ZoomClient] No video player found for userId ${userId}`)
      return
    }
    
    try {
      const mediaStream = this.zoomClient?.getMediaStream()
      if (mediaStream && typeof mediaStream.detachVideo === 'function') {
        await mediaStream.detachVideo(userId)
        console.log(`[ZoomClient] Detached video for userId ${userId}`)
      }
    } catch (error) {
      console.warn(`[ZoomClient] Error detaching video:`, error)
    } finally {
      this.videoPlayerRefs.delete(userId)
    }
  }

  /**
   * @deprecated Use attachVideo() instead. Kept for backward compatibility only.
   * renderVideo throws error 6112 on Chromium (no SharedArrayBuffer) in SDK v2.x.
   */
  async renderVideo(
    videoElement: HTMLVideoElement,
    userId: number,
    width: number,
    height: number
  ): Promise<void> {
    console.warn('[ZoomClient] renderVideo() is deprecated on Chromium — use attachVideo()')
    // Attempt renderVideo anyway (works when SharedArrayBuffer is available)
    if (!this.zoomClient) return
    try {
      const mediaStream = this.zoomClient.getMediaStream()
      await mediaStream.renderVideo(videoElement, userId, width, height, 0, 0, 3)
      console.log(`[ZoomClient] renderVideo succeeded for user ${userId}`)
    } catch (error) {
      console.error('[ZoomClient] Failed to render video:', error)
    }
  }

  // ==============================
  // Recording Methods (Cloud Recording)
  // ==============================

  /**
   * Start cloud recording for the current session
   * PREREQUISITE: Account must have cloud recording enabled in Zoom dashboard
   * Pattern from: https://developers.zoom.us/docs/video-sdk/web/recording/
   */
  async startRecording(): Promise<void> {
    if (!this.recordingClient) {
      throw new Error('Recording client not available')
    }

    try {
      await this.recordingClient.startCloudRecording()
      console.log('[ZoomClient] Cloud recording started')
      this.emitEvent('recording-started')
    } catch (error) {
      console.error('[ZoomClient] Failed to start recording:', error)
      throw error
    }
  }

  /**
   * Stop cloud recording for the current session
   */
  async stopRecording(): Promise<void> {
    if (!this.recordingClient) {
      throw new Error('Recording client not available')
    }

    try {
      await this.recordingClient.stopCloudRecording()
      console.log('[ZoomClient] Cloud recording stopped')
      this.emitEvent('recording-stopped')
    } catch (error) {
      console.error('[ZoomClient] Failed to stop recording:', error)
      throw error
    }
  }

  /**
   * Pause cloud recording
   */
  async pauseRecording(): Promise<void> {
    if (!this.recordingClient) {
      throw new Error('Recording client not available')
    }

    try {
      await this.recordingClient.pauseCloudRecording()
      console.log('[ZoomClient] Cloud recording paused')
      this.emitEvent('recording-paused')
    } catch (error) {
      console.error('[ZoomClient] Failed to pause recording:', error)
      throw error
    }
  }

  /**
   * Resume cloud recording
   */
  async resumeRecording(): Promise<void> {
    if (!this.recordingClient) {
      throw new Error('Recording client not available')
    }

    try {
      await this.recordingClient.resumeCloudRecording()
      console.log('[ZoomClient] Cloud recording resumed')
      this.emitEvent('recording-resumed')
    } catch (error) {
      console.error('[ZoomClient] Failed to resume recording:', error)
      throw error
    }
  }

  /**
   * Get current recording status
   * Returns: 'Recording' | 'Paused' | 'Stopped' | ''
   */
  getRecordingStatus(): string {
    if (!this.recordingClient) {
      return ''
    }

    try {
      return this.recordingClient.getCloudRecordingStatus() || ''
    } catch (error) {
      console.warn('[ZoomClient] Failed to get recording status:', error)
      return ''
    }
  }

  // ==============================
  // Closed-Captioning Methods (Zoom Native)
  // ==============================

  /**
   * Enable closed captions with automatic speech recognition
   * Zoom SDK provides built-in ASR (Automatic Speech Recognition) for real-time transcription
   * 
   * Supported languages: en-US, es-ES, de-DE, fr-FR, pt-PT, ru-RU, ja-JP, zh-CN, ko-KR, and more
   * 
   * PREREQUISITES:
   * - Host must enable "Closed captioning" in Zoom dashboard
   * - Account must have "Full transcript" feature enabled
   * - Zoom SDK version 2.2.10+ (LiveTranscriptionClient API)
   * 
   * Pattern from: https://marketplacefront.zoom.us/sdk/custom/web/modules/ZoomVideo.LiveTranscriptionClient.html
   */
  async enableClosedCaptions(language: string = 'en'): Promise<void> {
    if (!this.liveTranscriptionClient) {
      throw new Error('Live transcription client not available. This Zoom SDK version may not support captions, or captions are not enabled for your account.')
    }

    try {
      console.log(`[ZoomClient] Starting live transcription with language: ${language}`)
      
      // Refresh client reference
      if (this.zoomClient && typeof this.zoomClient.getLiveTranscriptionClient === 'function') {
        const freshClient = this.zoomClient.getLiveTranscriptionClient()
        
        if (freshClient && typeof freshClient.startLiveTranscription === 'function') {
          this.liveTranscriptionClient = freshClient
          
          // Attempt to start live transcription directly
          console.log('[ZoomClient] Calling startLiveTranscription()...')
          const startResult = await this.liveTranscriptionClient.startLiveTranscription()
          console.log('[ZoomClient] Live transcription started:', startResult)
          
          // Check result for known error states
          if (startResult && startResult.type === 'IMPROPER_MEETING_STATE') {
            throw new Error('IMPROPER_MEETING_STATE: Live transcription feature not enabled')
          }
          
          // Set the speaking language (2-letter ISO 639-1 code)
          console.log('[ZoomClient] Calling setSpeakingLanguage()...')
          const langResult = await this.liveTranscriptionClient.setSpeakingLanguage(language)
          console.log(`[ZoomClient] Speaking language set to ${language}:`, langResult)
          
          this.emitEvent('caption-enabled')
        } else {
          throw new Error('startLiveTranscription method not available on client')
        }
      } else {
        throw new Error('Cannot refresh LiveTranscriptionClient - ZoomClient not available')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error('[ZoomClient] Failed to enable live transcription:', error)
      console.error('[ZoomClient] Error type:', error instanceof TypeError ? 'TypeError' : typeof error)
      
      // Provide user-friendly error messages based on error type
      if (errorMessage.includes('undefined') || error instanceof TypeError) {
        throw new Error(
          'Live transcription is not available for this session.\n\n' +
          'Required Zoom account settings:\n' +
          '1. Enable "Full transcript" feature in your Zoom account\n' +
          '2. Enable "Closed captioning" in account settings\n' +
          '3. Ensure you are the session host or have transcription permissions\n\n' +
          'Note: This feature requires a Zoom Business, Education, or Enterprise account.'
        )
      }
      
      if (errorMessage.includes('IMPROPER_MEETING_STATE')) {
        throw new Error(
          'Live transcription cannot be started in this session.\n' +
          'The feature may not be enabled for your Zoom account.'
        )
      }
      
      throw error
    }
  }

  /**
   * Disable closed captions
   */
  async disableClosedCaptions(): Promise<void> {
    if (!this.liveTranscriptionClient) {
      throw new Error('Live transcription client not available')
    }

    try {
      // Note: Zoom Web SDK does not provide stopLiveTranscription() method
      // Transcription continues until session ends. We just stop displaying it in UI.
      console.log('[ZoomClient] Live transcription disabled (UI-level only)')
      this.emitEvent('caption-disabled')
    } catch (error) {
      console.error('[ZoomClient] Failed to disable live transcription:', error)
      throw error
    }
  }

  /**
   * Check if closed captions are supported
   * Returns true only if live transcription client is available
   */
  supportsClosedCaptions(): boolean {
    return this.liveTranscriptionClient !== null
  }

  /**
   * Get available caption languages
   * Zoom SDK supports multiple languages for ASR
   * 
   * Note: Actual availability depends on account settings
   */
  getAvailableCaptionLanguages(): string[] {
    return [
      'en', // English
      'es', // Spanish
      'de', // German
      'fr', // French
      'pt', // Portuguese
      'ru', // Russian
      'ja', // Japanese
      'zh', // Chinese (Simplified)
      'ko', // Korean
      'it', // Italian
      'nl', // Dutch
      'ar', // Arabic
    ]
  }

  /**
   * Emit remote-stream-added for any participants already sending video when we join.
   * peer-video-state-change only fires for changes that occur while we are in the
   * session, so if the nurse started video before the room endpoint joined, that
   * event is missed. This method polls getAllUser() after a short settling delay.
   */
  private emitExistingVideoStreams(): void {
    // Poll for existing video streams in two passes:
    // 1st pass at 2s: catches participants who joined before us and already started video
    // 2nd pass at 5s: catches participants whose video state takes longer to propagate
    const check = () => {
      if (!this.zoomClient) return
      try {
        const currentUser = this.zoomClient.getCurrentUserInfo()
        const participants = this.zoomClient.getAllUser()
        console.log('[ZoomClient] Checking existing video streams', {
          participantCount: participants?.length ?? 0,
        })
        participants?.forEach((participant: any) => {
          if (participant.userId === currentUser?.userId) return
          if (participant.bVideoOn && !this.remoteStreams.has(participant.userId)) {
            console.log(`[ZoomClient] Found pre-existing video stream for user ${participant.userId}`)
            const remoteStream = Object.assign(new MediaStream(), {
              __isZoomStream: true,
              __zoomClient: this,
              __userId: participant.userId,
            }) as MediaStream
            this.remoteStreams.set(participant.userId, remoteStream)
            this.emitEvent('remote-stream-added', remoteStream)
          }
        })
      } catch (err) {
        console.warn('[ZoomClient] Could not check existing video streams:', err)
      }
    }

    setTimeout(check, 2000)
    setTimeout(check, 5000)
  }

  /**
   * Start local video
   */
  async startVideo(): Promise<void> {
    if (!this.zoomClient) {
      return
    }

    try {
      const mediaStream = this.zoomClient.getMediaStream()
      await mediaStream.startVideo()
      console.log('[ZoomClient] Video started')
    } catch (error) {
      console.error('[ZoomClient] Failed to start video:', error)
      throw error
    }
  }

  /**
   * Stop local video
   */
  async stopVideo(): Promise<void> {
    if (!this.zoomClient) {
      return
    }

    try {
      const mediaStream = this.zoomClient.getMediaStream()
      await mediaStream.stopVideo()
      console.log('[ZoomClient] Video stopped')
    } catch (error: any) {
      // Non-fatal errors during cleanup (camera already closed, session ended)
      const knownCodes = [6109, 5002] // 6109 = camera closed, 5002 = meeting closed
      if (knownCodes.includes(error?.errorCode)) {
        console.log('[ZoomClient] stopVideo: camera already stopped')
        return
      }
      console.error('[ZoomClient] Failed to stop video:', error)
      throw error
    }
  }

  /**
   * Mute local audio
   */
  async muteAudio(): Promise<void> {
    if (!this.zoomClient) {
      return
    }

    try {
      const mediaStream = this.zoomClient.getMediaStream()
      await mediaStream.muteAudio()
      console.log('[ZoomClient] Audio muted')
    } catch (error: any) {
      // Non-fatal errors during cleanup (no audio joined, session ended)
      const knownCodes = [6015, 5002] // 6015 = no audio joined, 5002 = meeting closed
      if (knownCodes.includes(error?.errorCode)) {
        console.log('[ZoomClient] muteAudio: audio already muted or not joined')
        return
      }
      console.error('[ZoomClient] Failed to mute audio:', error)
      throw error
    }
  }

  /**
   * Unmute local audio
   */
  async unmuteAudio(): Promise<void> {
    if (!this.zoomClient) {
      return
    }

    try {
      const mediaStream = this.zoomClient.getMediaStream()
      try {
        await mediaStream.unmuteAudio()
      } catch (error: any) {
        // If audio engine has not started yet, start it once and retry unmute.
        const code = Number(error?.errorCode ?? -1)
        if (code === 6011 || code === 6015 || String(error?.reason || '').includes('loading')) {
          await this.startAudioWithRetry(mediaStream, 1, 6000, this._mediaAbortGeneration)
          await mediaStream.unmuteAudio()
        } else {
          throw error
        }
      }
      console.log('[ZoomClient] Audio unmuted')
    } catch (error) {
      console.error('[ZoomClient] Failed to unmute audio:', error)
      throw error
    }
  }

  /**
   * Get remote media streams
   */
  getRemoteStreams(): MediaStream[] {
    return Array.from(this.remoteStreams.values())
  }

  /**
   * Register event listener
   */
  on(event: VideoClientEvent, handler: (...args: any[]) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set())
    }
    this.eventHandlers.get(event)!.add(handler)
  }

  /**
   * Unregister event listener
   */
  off(event: VideoClientEvent, handler: (...args: any[]) => void): void {
    const handlers = this.eventHandlers.get(event)
    if (handlers) {
      handlers.delete(handler)
    }
  }

  /**
   * Clean up resources
   */
  async dispose(): Promise<void> {
    console.log('[ZoomClient] Disposing ZoomClient...')
    
    // 1. MANDATORY: Detach all video elements first
    const userIds = Array.from(this.videoPlayerRefs.keys())
    for (const userId of userIds) {
      await this.detachVideo(userId)
    }
    
    // 2. Remove all event listeners (prevents memory leaks)
    this.removeEventListeners()
    this.removeRuntimeErrorGuards()
    
    // 3. Leave session (this will stop media)
    await this.leaveSession()
    
    // 4. Clear state
    this.initialized = false
    this.zoomClient = null
    this.eventHandlers.clear()
    this.remoteStreams.clear()
    
    console.log('[ZoomClient] Disposed')
  }

  // Private helper methods

  private setupEventListeners(): void {
    if (!this.zoomClient) {
      return
    }

    // Store bound handlers for proper cleanup
    const onPeerVideoStateChange = async (payload: any) => {
      console.log('[ZoomClient] Participant video state changed:', payload)
      
      if (payload.action === 'Start') {
        const remoteStream = Object.assign(new MediaStream(), {
          __isZoomStream: true,
          __zoomClient: this,
          __userId: payload.userId,
        }) as MediaStream

        this.remoteStreams.set(payload.userId, remoteStream)
        
        // Emit remote stream added event for compatibility with useVideoClient composable
        this.emitEvent('remote-stream-added', remoteStream)
      } else if (payload.action === 'Stop') {
        // Remove remote stream when participant stops video
        // MANDATORY: Detach video element first
        if (this.videoPlayerRefs.has(payload.userId)) {
          await this.detachVideo(payload.userId)
        }
        
        const remoteStream = this.remoteStreams.get(payload.userId)
        if (remoteStream) {
          this.remoteStreams.delete(payload.userId)
          this.emitEvent('remote-stream-removed', remoteStream)
        }
      }
    }

    const onUserRemoved = async (payload: any) => {
      console.log('[ZoomClient] Participant left:', payload)
      this.emitEvent('participant-left', payload)
      
      // MANDATORY: Detach video element before removing stream
      if (this.videoPlayerRefs.has(payload.userId)) {
        await this.detachVideo(payload.userId)
      }
      
      // Remove from remote streams
      const remoteStream = this.remoteStreams.get(payload.userId)
      if (remoteStream) {
        this.remoteStreams.delete(payload.userId)
        this.emitEvent('remote-stream-removed', remoteStream)
      }
    }

    const onConnectionChange = (payload: any) => {
      console.log('[ZoomClient] Connection state changed:', payload)
      
      if (payload.state === 'Closed' || payload.state === 'Failed') {
        this.emitEvent('disconnected')
      }
    }

    const onCaptionMessage = (payload: any) => {
      console.log('[ZoomClient] Live transcription message received:', payload)
      
      // Zoom SDK live transcription payload structure:
      // { text: string, timestamp: number, userId: number, done: boolean }
      const captionData = {
        text: payload.text || payload.msg || '',
        timestamp: payload.timestamp || Date.now(),
        isFinal: payload.done || payload.isFinal || false,
        speakerId: payload.userId?.toString() || payload.speakerId,
        language: payload.language || undefined,
      }
      
      this.emitEvent('caption-received', captionData)
    }

    // Register event listeners
    this.zoomClient.on('peer-video-state-change', onPeerVideoStateChange)
    this.zoomClient.on('user-removed', onUserRemoved)
    this.zoomClient.on('connection-change', onConnectionChange)
    
    // Register live transcription event listener (only if transcription client is available)
    if (this.liveTranscriptionClient) {
      this.zoomClient.on('caption-message', onCaptionMessage)
      this.boundHandlers.set('caption-message', onCaptionMessage)
    }

    // Store bound handlers for cleanup
    this.boundHandlers.set('peer-video-state-change', onPeerVideoStateChange)
    this.boundHandlers.set('user-removed', onUserRemoved)
    this.boundHandlers.set('connection-change', onConnectionChange)
  }

  /**
   * Remove all event listeners (MANDATORY for cleanup)
   * Pattern from: https://github.com/zoom/videosdk-web-sample/blob/main/src/feature/video/hooks/useParticipantsChange.ts#L17-L26
   */
  private removeEventListeners(): void {
    if (!this.zoomClient) return

    this.boundHandlers.forEach((handler, eventName) => {
      try {
        this.zoomClient.off(eventName, handler)
        console.log(`[ZoomClient] Removed listener: ${eventName}`)
      } catch (error) {
        console.warn(`[ZoomClient] Error removing listener ${eventName}:`, error)
      }
    })

    this.boundHandlers.clear()
  }

  private emitEvent(event: VideoClientEvent, ...args: any[]): void {
    const handlers = this.eventHandlers.get(event)
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(...args)
        } catch (error) {
          console.error(`[ZoomClient] Error in event handler for ${event}:`, error)
        }
      })
    }
  }

  private removeRuntimeErrorGuards(): void {
    if (typeof window === 'undefined') return

    if (this.runtimeErrorHandler) {
      window.removeEventListener('error', this.runtimeErrorHandler, true)
      this.runtimeErrorHandler = null
    }

    if (this.runtimeRejectionHandler) {
      window.removeEventListener('unhandledrejection', this.runtimeRejectionHandler)
      this.runtimeRejectionHandler = null
    }
  }

  /**
   * Zoom SDK can transiently throw during getMediaStream() immediately after join().
   * Retry on known transient signatures to avoid race conditions in SDK internals.
   */
  private static readonly ZOOM_MEDIA_ABORTED = 'ZOOM_MEDIA_ABORTED'

  private async getZoomMediaStreamWithRetry(maxAttempts = 12, mediaGen?: number): Promise<any> {
    if (!this.zoomClient) {
      throw new Error('Zoom client not initialized')
    }

    let delayMs = 250
    let lastError: any

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (mediaGen !== undefined && this._mediaAbortGeneration !== mediaGen) {
        throw new Error(ZoomClient.ZOOM_MEDIA_ABORTED)
      }
      if (!this.zoomClient) {
        throw new Error('Zoom client not initialized')
      }

      try {
        const stream = this.zoomClient.getMediaStream()
        if (stream) return stream
      } catch (error: any) {
        lastError = error
        const msg = String(error?.message || error || '')
        const isTransient =
          msg.includes('shouldUseWebRTC') ||
          msg.includes("reading 'util'") ||
          msg.includes("reading 'caps'") ||
          msg.includes('isSupportVideoDecodeHardwareAcceleration')

        if (!isTransient || attempt === maxAttempts) {
          throw error
        }
      }

      await new Promise(resolve => setTimeout(resolve, delayMs))
      if (mediaGen !== undefined && this._mediaAbortGeneration !== mediaGen) {
        throw new Error(ZoomClient.ZOOM_MEDIA_ABORTED)
      }
      delayMs = Math.min(delayMs * 1.6, 2000)
    }

    throw lastError || new Error('Failed to get Zoom media stream')
  }

  private isMediaStillLoadingError(error: any): boolean {
    const errorCode = Number(error?.errorCode ?? -1)
    const reason = String(error?.reason || error?.message || '').toLowerCase()
    const opType = String(error?.type ?? '')
    return (
      opType === 'OPERATION_TIMEOUT' || // Zoom SDK internal timeout on startAudio (often without SAB)
      errorCode === 6011 || // audio is still loading
      errorCode === 6105 || // camera is still starting
      errorCode === 6001 || // camera not started yet
      reason.includes('please wait') ||
      reason.includes('loading') ||
      reason.includes('starting')
    )
  }

  /**
   * After a failed startAudio attempt, calling stopAudio() resets the pipeline.
   * Do NOT stop when audio was still loading, or when we aborted a hung promise
   * with a timeout — stopping then makes 6011 loops worse (seen in nurse console).
   */
  private shouldSkipStopAudioAfterStartFailure(error: unknown): boolean {
    if (this.isMediaStillLoadingError(error)) return true
    const msg = String((error as { message?: string })?.message ?? error ?? '')
    if (msg.includes('timed out')) return true
    return false
  }

  /**
   * Zoom expects computer audio to start before parallel getMediaStream / attachVideo work.
   * Remote tiles can mount before getLocalMedia() finishes; without this, startAudio races
   * with remote attach and returns 6011 ("Computer audio has been loading").
   */
  private async ensureComputerAudioStarted(): Promise<void> {
    if (!this.zoomClient) return
    if (this._computerAudioEnsurePromise) {
      return this._computerAudioEnsurePromise
    }

    this._computerAudioEnsurePromise = (async () => {
      const mediaGen = this._mediaAbortGeneration
      const stillValid = (): boolean =>
        this._mediaAbortGeneration === mediaGen &&
        _zoomSessionActive &&
        this.zoomClient != null

      if (!stillValid()) return

      await this.ensurePrimedAudioContext()
      if (!stillValid()) return

      let mediaStream: any
      try {
        mediaStream = await this.getZoomMediaStreamWithRetry(12, mediaGen)
      } catch (e) {
        if (e instanceof Error && e.message === ZoomClient.ZOOM_MEDIA_ABORTED) return
        throw e
      }
      if (!stillValid()) return

      await new Promise<void>(resolve => setTimeout(resolve, 400))
      if (!stillValid()) return

      console.log('[ZoomClient] Starting computer audio (before video attach)...')
      try {
        // Without cross-origin isolation, Zoom's audio WASM can take >25s; do not use short races.
        await this.startAudioWithRetry(mediaStream, 10, 90000, mediaGen)
        if (stillValid()) {
          console.log('[ZoomClient] Computer audio started')
        }
      } catch (audioErr: any) {
        if (!stillValid()) return
        console.warn(
          '[ZoomClient] startAudio did not complete:',
          audioErr?.message || audioErr,
          '— mic may need unmute / retry'
        )
      }
    })()

    return this._computerAudioEnsurePromise
  }

  private async ensurePrimedAudioContext(): Promise<void> {
    if (typeof window === 'undefined') return

    try {
      const win = window as any
      const AudioContextCtor = win.AudioContext || win.webkitAudioContext
      if (!AudioContextCtor) return

      if (!win.__vcCallAudioContext) {
        win.__vcCallAudioContext = new AudioContextCtor()
      }

      const ctx = win.__vcCallAudioContext as AudioContext | undefined
      if (ctx && ctx.state === 'suspended') {
        await ctx.resume()
      }
    } catch (error) {
      console.warn('[ZoomClient] Failed to ensure primed audio context:', error)
    }
  }

  /** Meeting ended or leave() ran — do not retry startAudio. */
  private isSessionClosedAudioError(error: unknown): boolean {
    const e = error as { errorCode?: number; reason?: string; type?: string }
    if (e?.errorCode === 5002) return true
    if (String(e?.type ?? '') === 'IMPROPER_MEETING_STATE') return true
    return String(e?.reason ?? '').toLowerCase() === 'closed'
  }

  private async startAudioWithRetry(
    mediaStream: any,
    attempts = 1,
    timeoutMs = 30000,
    mediaGen?: number
  ): Promise<void> {
    let lastError: any
    let delayMs = 500

    for (let attempt = 1; attempt <= attempts; attempt++) {
      if (mediaGen !== undefined && this._mediaAbortGeneration !== mediaGen) {
        return
      }
      try {
        await this.ensurePrimedAudioContext()
        await Promise.race([
          mediaStream.startAudio(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('startAudio() timed out')), timeoutMs)),
        ])
        return
      } catch (error: any) {
        if (mediaGen !== undefined && this._mediaAbortGeneration !== mediaGen) {
          return
        }
        if (this.isSessionClosedAudioError(error)) {
          console.log('[ZoomClient] startAudio aborted: session closed or leaving')
          return
        }
        lastError = error
        console.warn(`[ZoomClient] startAudio attempt ${attempt}/${attempts} failed`, error?.message || error)
        if (attempt < attempts) {
          // 6011 / timeout: do NOT stopAudio() — that aborts an in-flight load and worsens 6011.
          if (
            !this.shouldSkipStopAudioAfterStartFailure(error) &&
            typeof mediaStream.stopAudio === 'function'
          ) {
            try {
              await mediaStream.stopAudio()
            } catch {
              // best-effort reset only
            }
          }
          const waitMs = this.shouldSkipStopAudioAfterStartFailure(error)
            ? Math.max(delayMs, 2000)
            : delayMs
          await new Promise(resolve => setTimeout(resolve, waitMs))
          if (mediaGen !== undefined && this._mediaAbortGeneration !== mediaGen) {
            return
          }
          delayMs = this.shouldSkipStopAudioAfterStartFailure(error)
            ? Math.min(delayMs * 1.7, 8000)
            : Math.min(delayMs * 2, 2500)
        }
      }
    }

    throw lastError || new Error('startAudio failed')
  }

}

/**
 * Implementation Notes:
 * 
 * 1. Zoom Video SDK Web:
 *    - Requires @zoom/videosdk package
 *    - SDK handles WebRTC internally
 *    - Provides higher-level APIs than raw WebRTC
 * 
 * 2. Session Joining:
 *    - Requires JWT token from backend
 *    - Session name used as identifier
 *    - Optional password for additional security
 * 
 * 3. Media Management:
 *    - Zoom SDK provides MediaStream API
 *    - Start/stop video and audio separately
 *    - Can render to HTML video elements
 * 
 * 4. Events:
 *    - Zoom SDK emits various events
 *    - We map them to our VideoClientEvent types
 *    - Provides consistent interface across providers
 * 
 * 5. Browser Compatibility:
 *    - Chrome 74+
 *    - Firefox 66+
 *    - Safari 12+
 *    - Edge 79+
 * 
 * References:
 * - Zoom Video SDK for Web: https://developers.zoom.us/docs/video-sdk/web/
 * - API Reference: https://developers.zoom.us/docs/video-sdk/web/reference/
 */
